import { useMemo } from "react";
import { GlobalRole } from "@/api/session";
import { useRequest } from "@/request";
import type { PagedResultDto } from "@/types/abp";

export { GlobalRole };

/** 成员状态（对应后端 AgentStatus 枚举）。 */
export const AgentStatus = {
  Active: 1,
  Inactive: 2,
} as const;
export type AgentStatus = (typeof AgentStatus)[keyof typeof AgentStatus];

/** 客服成员（对应 Api.Admin AgentDto，JSON 为 camelCase）。 */
export type AgentDto = {
  id: string;
  iamSubject: string;
  displayName: string;
  /** IAM 登录名。邮箱在 IAM 侧允许重复，认人以它为准。 */
  userName: string;
  email: string | null;
  avatarUrl: string | null;
  callCenterExtension: string | null;
  skills: string[];
  status: AgentStatus;
  /** 全局角色；团队内角色见 `TeamMemberDto.role`。 */
  globalRoles: GlobalRole[];
  concurrencyStamp: string;
};

/** IAM 目录检索结果，附带该员工在 tickbird 的开通状态。 */
export type IamDirectoryUserDto = {
  subject: string;
  displayName: string;
  /** IAM 登录名。选人时靠它区分同名/同邮箱的人。 */
  userName: string;
  email: string | null;
  avatarUrl: string | null;
  /** 已开通时为本地 Agent.Id；null 表示尚未开通。 */
  agentId: string | null;
  agentStatus: AgentStatus | null;
};

export type AgentListRequest = {
  filter?: string;
  onlyActive?: boolean;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
};

/**
 * 从 IAM 目录开通人员。只提交 subject——姓名/邮箱/头像由服务端从 IAM 回读，
 * 前端无法也不应该伪造（docs/identity-and-authz.md §5.1）。
 */
export type ProvisionAgentInput = {
  iamSubject: string;
};

/**
 * 更新 tickbird 自己管的业务属性。姓名/邮箱/头像是 IAM 身份字段，
 * 每次登录会从 IAM 同步回来，故不在此处编辑。
 */
export type UpdateAgentInput = {
  callCenterExtension?: string | null;
  skills: string[];
  concurrencyStamp: string;
};

function buildQuery(request: AgentListRequest): string {
  const params = new URLSearchParams();
  if (request.filter) {
    params.set("Filter", request.filter);
  }
  if (request.onlyActive !== undefined) {
    params.set("OnlyActive", String(request.onlyActive));
  }
  if (request.skipCount !== undefined) {
    params.set("SkipCount", String(request.skipCount));
  }
  if (request.maxResultCount !== undefined) {
    params.set("MaxResultCount", String(request.maxResultCount));
  }
  if (request.sorting) {
    params.set("Sorting", request.sorting);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 客服成员客户端（挂在 Api.Admin `agents` 端点上；后端不支持删除）。 */
export function useAgentApi() {
  const { request } = useRequest();

  return useMemo(
    () => ({
      getList: (input: AgentListRequest = {}, signal?: AbortSignal) =>
        request<PagedResultDto<AgentDto>>(`agents${buildQuery(input)}`, { method: "GET", signal, skipErrorToast: true }),
      get: (id: string, signal?: AbortSignal) => request<AgentDto>(`agents/${id}`, { method: "GET", signal, skipErrorToast: true }),
      searchDirectory: (keyword: string, maxResultCount = 20) =>
        request<IamDirectoryUserDto[]>(`agents/directory?Keyword=${encodeURIComponent(keyword)}&MaxResultCount=${maxResultCount}`, {
          method: "GET",
          skipErrorToast: true,
        }),
      provision: (input: ProvisionAgentInput) => request<AgentDto>("agents/provision", { method: "POST", data: input }),
      grantRole: (id: string, role: GlobalRole) => request<AgentDto>(`agents/${id}/roles`, { method: "POST", data: { role } }),
      revokeRole: (id: string, role: GlobalRole) => request<AgentDto>(`agents/${id}/roles/${role}`, { method: "DELETE" }),
      update: (id: string, input: UpdateAgentInput) => request<AgentDto>(`agents/${id}`, { method: "PUT", data: input }),
      setActive: (id: string, active: boolean) => request<AgentDto>(`agents/${id}/active?active=${active}`, { method: "POST" }),
    }),
    [request],
  );
}
